import { RefObject, useCallback, useRef, useState } from 'react';

type EditorPreviewLayout = {
  targetSurfaceWidth?: number;
  targetSurfaceHeight?: number;
  targetSurfaceTop?: number;
  targetSurfaceBottom?: number;
};

type CaptureEditorPreviewLayoutOptions = {
  faceNumber: number;
  isSignAndSend: boolean;
};

const getPositiveFiniteValue = (value: number | undefined): number | undefined =>
  value !== undefined && Number.isFinite(value) && value > 0 ? value : undefined;

export const useEditorPreviewLayout = (): {
  captureEditorPreviewLayout: (options: CaptureEditorPreviewLayoutOptions) => void;
  editorWorkspaceRef: RefObject<HTMLDivElement>;
  previewLayout: EditorPreviewLayout;
  workspacePanelRef: RefObject<HTMLDivElement>;
} => {
  const workspacePanelRef = useRef<HTMLDivElement>(null);
  const editorWorkspaceRef = useRef<HTMLDivElement>(null);
  const [previewLayout, setPreviewLayout] = useState<EditorPreviewLayout>({});

  const captureEditorPreviewLayout = useCallback(({ faceNumber, isSignAndSend }: CaptureEditorPreviewLayoutOptions) => {
    const workspacePanel = workspacePanelRef.current;
    if (!workspacePanel) {
      setPreviewLayout({});
      return;
    }

    const canvasWrapper = workspacePanel.querySelector<HTMLElement>(`[data-card-face-number="${faceNumber}"]`);
    const renderedCanvas =
      canvasWrapper?.querySelector<HTMLElement>('.canvas-container') ??
      canvasWrapper?.querySelector<HTMLElement>('canvas');
    const canvasRect = renderedCanvas?.getBoundingClientRect() ?? canvasWrapper?.getBoundingClientRect();
    if (!canvasRect) {
      setPreviewLayout({});
      return;
    }

    const workspaceRect = editorWorkspaceRef.current?.getBoundingClientRect() ?? workspacePanel.getBoundingClientRect();
    const cssRenderedWidth = Number.parseFloat(
      getComputedStyle(workspacePanel).getPropertyValue('--card-rendered-width'),
    );
    const measuredWidth = getPositiveFiniteValue(canvasRect.width);
    const measuredHeight = getPositiveFiniteValue(canvasRect.height);
    const measuredTop = getPositiveFiniteValue(canvasRect.top - workspaceRect.top);
    const measuredBottom = getPositiveFiniteValue(workspaceRect.bottom - canvasRect.bottom);

    setPreviewLayout({
      targetSurfaceWidth: measuredWidth ?? getPositiveFiniteValue(cssRenderedWidth),
      targetSurfaceHeight: measuredHeight,
      targetSurfaceTop: measuredTop,
      targetSurfaceBottom: isSignAndSend ? measuredBottom ?? 0 : undefined,
    });
  }, []);

  return {
    captureEditorPreviewLayout,
    editorWorkspaceRef,
    previewLayout,
    workspacePanelRef,
  };
};
